import { AnalyticsEvent, type PromoCardClicked } from '@shipt/analytics-member-web'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { urlPathnameToLocationName } from '@/features/shared/analytics/utils'
import { type Promotion } from '@/features/shop/services/Campaigns/types'
import { track } from '@/features/shared/analytics/trackingQueue'
import { PromotionScopeType } from '@/features/shared/analytics/promotions'
import { type ShiptEventOptions } from '@shipt/analytics-core'
import { type ProductCampaignsPromotion } from '@/features/shop/services/Product/types'

export type PromoCardEvent = Omit<PromoCardClicked['properties'], 'display_format'> & {
  display_format?: DISPLAY_FORMAT
  ad_placement_id?: string
}

export interface PromoCardProps {
  displayFormat?: DISPLAY_FORMAT
  gridIndex?: number
  listIndex?: number
  shelfIndex?: number
  subLocation?: string
  nextLocation?: string
}

interface FeaturedPromoCardProps extends PromoCardProps {
  promotion: Promotion
}

export const getFeaturedPromoCardProperties = ({
  promotion,
  displayFormat,
  gridIndex,
  listIndex,
  subLocation,
  shelfIndex,
  nextLocation,
}: FeaturedPromoCardProps): PromoCardEvent => {
  return {
    location: urlPathnameToLocationName(),
    ...(subLocation && { display_sublocation: subLocation }),
    ...(displayFormat && { display_format: displayFormat }),
    grid_index: gridIndex,
    list_index: listIndex,
    shelf_index: shelfIndex,
    creative: promotion.promotion_params?.images?.[0]?.image_url,
    promotion_type_id: Number(promotion.promotion_params?.feature_promotion_group_id || -1),
    promotion_type_name: promotion.type ?? PromotionScopeType.featured_promotion,
    promotion_id: String(promotion.id || ''),
    promotion_name: promotion.display_name ?? '',
    ...(promotion.promotion_params?.category_id && {
      promotion_category_id: String(promotion.promotion_params.category_id || ''),
    }),
    ...(nextLocation && { next_location: nextLocation }),
  }
}

export interface CouponPromoCardProps extends PromoCardProps {
  coupon: { couponId: string; summary?: string }
  couponClipped?: boolean
  actionType?: 'clip' | 'shop' | 'coupon_terms' | 'coupon'
}

export const getCouponPromoCardProperties = ({
  coupon,
  displayFormat,
  gridIndex,
  listIndex,
  subLocation,
  couponClipped,
  actionType,
  shelfIndex,
}: CouponPromoCardProps): PromoCardEvent => {
  return {
    location: urlPathnameToLocationName(),
    ...(actionType && { action_type: actionType }),
    ...(subLocation && { display_sublocation: subLocation }),
    ...(displayFormat && { display_format: displayFormat }),
    grid_index: gridIndex,
    list_index: listIndex,
    shelf_index: shelfIndex,
    promotion_type_name: PromotionScopeType.coupon,
    coupon_id: coupon.couponId,
    coupon_name: coupon.summary || '',
    coupon_clipped: Boolean(couponClipped),
  }
}

export const trackPromoCardViewed = ({
  beacon_url,
  ...restProps
}: Omit<PromoCardEvent, 'location'>) =>
  track({
    eventName: AnalyticsEvent.PromoCardViewed,
    properties: {
      ...getPromoCardProperties(restProps),
      ...(beacon_url && { beacon_type: 'onViewBeacon', beacon_url }),
    },
  })

export const trackPromoCardClicked = (
  { beacon_url, ...restProps }: Omit<PromoCardEvent, 'location'>,
  eventOptions?: Partial<ShiptEventOptions>,
) =>
  track({
    eventName: AnalyticsEvent.PromoCardClicked,
    properties: {
      ...getPromoCardProperties(restProps),
      ...(beacon_url && { beacon_type: 'onClickBeacon', beacon_url }),
    },
    eventOptions,
  })

const getPromoCardProperties = (
  eventProperties: Omit<PromoCardEvent, 'location'>,
): PromoCardClicked['properties'] => {
  const { list_index, display_format } = eventProperties
  return {
    display_format: DISPLAY_FORMAT.HORIZONTAL,
    location: urlPathnameToLocationName(),
    ...(!display_format && !list_index && { list_index: 0 }),
    ...eventProperties,
  }
}

export const getTrackPromotion = ({
  promotion,
  ...eventProps
}: PromoCardProps & {
  promotion: ProductCampaignsPromotion | undefined
}) => {
  const properties = promotion
    ? getFeaturedPromoCardProperties({
        promotion: {
          id: promotion.id,
          display_name: promotion.display_title,
          type: undefined,
          promotion_params: {
            category_id: promotion.category_id,
          },
        },
        ...eventProps,
      })
    : null

  return {
    onView: () => {
      if (properties) trackPromoCardViewed(properties)
    },
    onClick: () => {
      if (properties) trackPromoCardClicked(properties)
    },
  }
}
